<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-card elevation="0" outlined class="mt-5">
        <v-sheet class="pa-4 d-md-flex">
          <v-sheet class="flex-sm-column">
            <p class="text-h4 ma-0">{{ terminologies.program | capitalize }}</p>
          </v-sheet>

          <v-sheet class="flex-sm-column my-3 my-md-0">
            <v-breadcrumbs class="pa-md-3 pa-0">
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'home', params: {} }"
                >Home</v-breadcrumbs-item
              >
              <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'program', params: {} }"
                active
              >
                {{ terminologies.program | capitalize }}
              </v-breadcrumbs-item>
            </v-breadcrumbs>
          </v-sheet>

          <v-spacer></v-spacer>

          <v-sheet class="flex-sm-column mt-n4">
            <v-row align="baseline">
              <v-col cols="12" md="5">
                <v-text-field
                  dense
                  v-model="searchTerms"
                  @keyup.enter="search"
                  @click:clear="clearSearch"
                  @click:append="search"
                  rounded
                  placeholder="Search Programs"
                  append-icon="mdi-magnify"
                  outlined
                  clearable
                  label="Search"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-checkbox
                  label="Hide Inactive"
                  v-model="hideInactive"
                  hide-details
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-btn
                  class="align-self-end align-self-lg-start text-capitalize"
                  color="primary"
                  block
                  text
                  outlined
                  @click="programDialog = true"
                >
                  <v-icon>mdi-plus</v-icon>
                  Add {{ terminologies.program }}
                </v-btn>
              </v-col>
            </v-row>
          </v-sheet>
        </v-sheet>
      </v-card>

      <v-card elevation="0" class="mt-5">
        <div class="table-container">
          <v-data-table
            :headers="headers"
            :items="filteredPrograms"
            :items-per-page="currentPageSize"
            :loading="!programProcessed"
            :server-items-length="pagination.totalSize"
            :page="pagination.current"
            :disable-pagination="
              !filteredPrograms || filteredPrograms.length < 1
            "
            :hide-default-header="
              !filteredPrograms || filteredPrograms.length < 1
            "
            :hide-default-footer="
              !filteredPrograms || filteredPrograms.length < 1
            "
            :options.sync="options"
            :footer-props="{
              itemsPerPageOptions: [15, 25, 50, 100, -1],
              itemsPerPageText: 'Rows per page:',
              itemsPerPageAllText: 'All',
            }"
            item-key="id"
            class="elevation-1"
            @update:options="pageChanged"
          >
            <template v-slot:item.name="{ item }">
              {{ item.name }} <span v-if="item.inactive">(Inactive)</span>
              <div v-if="!globalCenter.id" class="text-caption">
                {{ getCenterName(item.centerId) }}
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon class="mr-2" @click="edit(item)"> mdi-pencil </v-icon>
              <v-icon color="red" class="mr-2" @click="remove(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>
    <confirmation-dialog ref="confirm" />

    <v-row justify="center">
      <v-dialog v-model="programDialog" persistent max-width="600px">
        <v-card>
          <v-card-text>
            <program-form
              :key="programId"
              :id="programId"
              :isModal="true"
              @clicked="closeProgramDialog"
            ></program-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { capitalizeFirstLetter } from "../../utils/string";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");
const ProgramForm = () => import("@/components/program/ProgramForm.vue");

export default {
  name: "ClassroomPage",
  data() {
    return {
      pagination: {
        current: 1,
        pageSize: 15,
        totalSize: 100,
      },
      options: {
        sortBy: [],
        sortDesc: [],
        itemsPerPage: 15,
      },
      programList: [],
      showParentPage: true,
      hideInactive: false,
      headers: [
        {
          text: "Name",
          value: "name",
          align: "start",
          sortable: true,
        },
        {
          text: "Description",
          value: "description",
          align: "start",
          sortable: false,
        },
        {
          text: "Standard Rate",
          value: "standardRate",
          align: "start",
          sortable: true,
        },
        {
          text: "Payment Terms",
          value: "payTerm",
          align: "start",
          sortable: false,
        },
        {
          text: "Max. Capacity",
          value: "maxCapacity",
          align: "start",
          sortable: false,
        },

        { text: "Actions", value: "actions", align: "end", sortable: false },
      ],
      sort: {
        key: null,
      },
      searchTerms: "",
      programDialog: false,
      programId: null,
    };
  },
  filters: {
    capitalize(value) {
      return capitalizeFirstLetter(value);
    },
  },
  components: {
    ConfirmationDialog,
    ProgramForm,
  },
  computed: {
    ...mapGetters("program", ["programProcessed", "getPrograms"]),
    ...mapGetters("terminology", ["terminologies"]),
    ...mapGetters("auth", ["myCenters", "getCenterName"]),
    ...mapGetters("navigation", ["globalCenter"]),
    filteredPrograms() {
      let programs = this.hideInactive
        ? this.programList.filter((program) => !program.inactive)
        : this.programList;

      if (this.searchTerms && this.searchTerms.trim()) {
        const search = this.searchTerms.trim().toLowerCase();
        programs = programs.filter(
          (p) =>
            p.name?.toLowerCase().includes(search) ||
            p.description?.toLowerCase().includes(search),
        );
      }

      return programs;
    },

    currentPageSize() {
      return this.options.itemsPerPage === -1 ? -1 : this.options.itemsPerPage;
    },
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("program", ["deleteProgram", "fetchPrograms"]),
    view(item) {
      this.$router.push({ name: "programView", params: { id: item.id } });
    },
    edit(item) {
      this.programId = item.id;
      this.programDialog = true;
    },
    async remove(item) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this program?",
      );
      if (confirm) {
        this.deleteProgram(item.id)
          .then(() => {
            this.addSuccessMessage("program was deleted successfully");
            this.getResources();
          })
          .catch(({ data }) => {
            if (
              data.message ===
              "Program is in use by undeleted schedules and cannot be deleted."
            )
              this.addErrorMessage(
                `${data.message} You can set to inactive instead.`,
              );
            else this.addErrorMessage(data.message);
          });
      }
    },
    getResources() {
      const sortBy =
        this.options["sortBy"] && this.options["sortBy"].length > 0
          ? this.options.sortBy[0]
          : "id";
      const sortDesc =
        this.options["sortDesc"] && this.options["sortDesc"].length > 0
          ? this.options.sortDesc[0]
          : true;

      const pageSize =
        this.options.itemsPerPage === -1
          ? 1000000
          : this.options.itemsPerPage || this.pagination.pageSize;
      const currentPage =
        this.options.itemsPerPage === -1
          ? 0
          : this.pagination.pageIndex && this.pagination.pageIndex > 0
          ? this.pagination.pageIndex - 1
          : 0;

      const params = {
        page: currentPage,
        size: pageSize,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
        centerId: this.globalCenter.id,
      };

      if (this.searchTerms && this.searchTerms.trim()) {
        params.search = this.searchTerms.trim();
      }

      this.fetchPrograms(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            this.programList = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    showParent(show) {
      this.showParentPage = show;
    },
    pageChanged(pagination) {
      this.pagination.pageIndex = pagination.page;
      this.pagination.pageSize = pagination.itemsPerPage;

      this.options.itemsPerPage = pagination.itemsPerPage;

      this.getResources();
    },
    search() {
      this.pagination.pageIndex = 1;
      this.options.page = 1;
      this.getResources();
    },
    clearSearch() {
      this.searchTerms = null;

      this.pagination.pageIndex = 1;
      this.options.page = 1;
      this.getResources();
    },
    closeProgramDialog() {
      this.programDialog = false;
      this.programId = null;
      this.getResources();
    },
  },
  watch: {
    "pagination.numberRegisterForPage": function () {
      this.pagination.current = -1;
    },
    hideInactive() {
      // Refresh data when hide inactive checkbox changes
      this.getResources();
    },
  },
  mounted() {
    this.getResources();
  },
  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) {
      this.getResources();
    }
    next();
  },
};
</script>
